/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Eye, ArrowRight, ArrowLeft, Users, User, UserCheck, Glasses, Sun, HelpCircle, ShoppingBag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import SalesLayout from '../../components/layout/SalesLayout';

// Styled component for icon images
const IconImage = styled.img`
  width: ${props => props.size || '28px'};
  height: ${props => props.size || '28px'};
  object-fit: contain;
  opacity: ${props => props.selected ? 1 : 0.7};
  transition: opacity 0.2s ease;
  display: block;
`;

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #fafbfc;
  padding: 0;
  position: relative;
  overflow-x: hidden;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
`;

const BottomBar = styled.div`
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  padding: 1rem 2.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.08);
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  margin-top: auto;
  
  @media (max-width: 768px) {
    padding: 0.875rem 1.5rem;
  }
`;

const SalesPersonInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const BadgeDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.875rem;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }
`;

const SalesPersonName = styled.span`
  font-weight: 600;
  color: #1e293b;
  font-size: 0.95rem;
  letter-spacing: 0.2px;
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);

  &:hover {
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 2rem;
  position: relative;
  z-index: 1;
  width: 100%;
  background: #fafbfc;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Card = styled.div`
  background: #ffffff;
  padding: 2rem;
  width: 100%;
  max-width: 100%;
  position: relative;
  border-radius: 20px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 16px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing['2xl']};
`;

const Logo = styled.div`
  width: 90px;
  height: 90px;
  border-radius: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.white};
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.4);
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05) rotate(5deg);
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 ${props => props.theme.spacing.sm} 0;
  letter-spacing: -0.02em;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: #64748b;
  margin: 0;
  font-weight: 400;
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  position: relative;
  
  &:not(:last-child) {
    margin-bottom: 0.25rem;
  }
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #334155;
  letter-spacing: 0;
  margin-bottom: 0.5rem;
  display: block;
  line-height: 1.4;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 16px;
  font-size: 0.875rem;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #1e293b;
  font-family: inherit;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23667eea' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  padding-right: 2.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.1);

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1), 0 1px 3px rgba(0, 0, 0, 0.05);
  }

  &:hover:not(:focus) {
    border-color: #d1d5db;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.1);

  &:hover {
    border-color: #667eea;
    background: #667eea08;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  }

  input[type="checkbox"]:checked ~ & {
    border-color: #667eea;
    background: #667eea0d;
    box-shadow: 0 2px 4px rgba(102, 126, 234, 0.15);
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #667eea;
`;

const CheckboxLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: #1e293b;
  cursor: pointer;
  user-select: none;
`;

const CompanionSection = styled.div`
  background: #f9fafb;
  padding: 1rem;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  display: ${props => props.show ? 'flex' : 'none'};
  flex-direction: column;
  gap: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.1);
  animation: ${props => props.show ? 'fadeIn 0.3s ease' : 'none'};
  flex: 1;
  min-width: 0;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateX(-10px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

const CompanionTitle = styled.h3`
  font-size: 0.75rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding-bottom: 0.375rem;
  border-bottom: 1px solid #e2e8f0;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 16px;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  background: #ffffff;
  color: #1e293b;
  font-family: inherit;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.1);

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1), 0 1px 3px rgba(0, 0, 0, 0.05);
    background: #ffffff;
  }

  &:hover:not(:focus) {
    border-color: #d1d5db;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 0.75rem 1.5rem;
  background: ${props => props.disabled
    ? '#cbd5e1'
    : `linear-gradient(135deg, ${props.genderColor || '#667eea'} 0%, ${props.genderColor === '#ec4899' ? '#db2777' : props.genderColor === '#3b82f6' ? '#2563eb' : '#764ba2'} 100%)`};
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: ${props => props.disabled
    ? 'none'
    : `0 4px 15px ${props.genderColor || '#667eea'}66`};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${props => `0 8px 25px ${props.genderColor || '#667eea'}80`};
    
    &::before {
      left: 100%;
    }
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: ${props => `0 4px 15px ${props.genderColor || '#667eea'}66`};
  }

  &:disabled {
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const bounce = keyframes`
  0%, 100% {
    transform: translateY(0) scale(1);
  }
  50% {
    transform: translateY(-10px) scale(1.05);
  }
`;

const malePulse = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1) rotate(0deg);
    box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7);
  }
  50% {
    opacity: 0.9;
    transform: scale(1.15) rotate(5deg);
    box-shadow: 0 0 20px 10px rgba(59, 130, 246, 0.4);
  }
`;

const femalePulse = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1) rotate(0deg);
    box-shadow: 0 0 0 0 rgba(236, 72, 153, 0.7);
  }
  50% {
    opacity: 0.9;
    transform: scale(1.2) rotate(-5deg);
    box-shadow: 0 0 25px 12px rgba(236, 72, 153, 0.4);
  }
`;

const maleGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 10px rgba(59, 130, 246, 0.5), 0 0 20px rgba(59, 130, 246, 0.3);
  }
  50% {
    box-shadow: 0 0 20px rgba(59, 130, 246, 0.8), 0 0 40px rgba(59, 130, 246, 0.5);
  }
`;

const femaleGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 10px rgba(236, 72, 153, 0.5), 0 0 20px rgba(236, 72, 153, 0.3);
  }
  50% {
    box-shadow: 0 0 20px rgba(236, 72, 153, 0.8), 0 0 40px rgba(236, 72, 153, 0.5);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const wiggle = keyframes`
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(-3deg); }
  75% { transform: rotate(3deg); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.9;
  }
`;

const TrackingSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const TrackingTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
`;

const TrackingInputGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const TrackingInput = styled(Input)`
  flex: 1;
`;

const TrackButton = styled(Button)`
  padding: 0.875rem 1.5rem;
  width: auto;
  min-width: 120px;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  color: #dc2626;
  padding: 1rem;
  border-radius: 12px;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: transparent;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.2s;
  
  &:hover {
    background: #f1f5f9;
    color: #1e293b;
  }
`;

const ScannerContainer = styled.div`
  position: relative;
  width: 100%;
  height: 300px;
  background: #f1f5f9;
  border: 2px dashed #cbd5e1;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  overflow: hidden;
`;

const ScannerVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
`;

const ScannerOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 250px;
  height: 250px;
  border: 3px solid #667eea;
  border-radius: 12px;
  background: transparent;
  pointer-events: none;
  z-index: 3;
  
  &::before {
    content: '';
    position: absolute;
    top: -3px;
    left: -3px;
    right: -3px;
    bottom: -3px;
    border: 3px solid #667eea;
    border-radius: 12px;
    animation: pulse 2s infinite;
  }
`;

const ScannerPlaceholder = styled.div`
  text-align: center;
  color: #64748b;
  position: relative;
  z-index: 2;
`;

const ScannerIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #e0e7ff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  color: #667eea;
`;

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: ${props => props.active ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'white'};
  color: ${props => props.active ? 'white' : '#667eea'};
  border: 2px solid #667eea;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 1rem;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(102, 126, 234, 0.2);
  }
`;

const childBounce = keyframes`
  0%, 100% {
    transform: translateY(0) scale(1) rotate(0deg);
  }
  25% {
    transform: translateY(-8px) scale(1.05) rotate(-2deg);
  }
  50% {
    transform: translateY(-12px) scale(1.1) rotate(0deg);
  }
  75% {
    transform: translateY(-8px) scale(1.05) rotate(2deg);
  }
`;

const teenShake = keyframes`
  0%, 100% {
    transform: translateY(0) scale(1) rotate(0deg);
  }
  10%, 30%, 50%, 70%, 90% {
    transform: translateY(-5px) scale(1.08) rotate(-3deg);
  }
  20%, 40%, 60%, 80% {
    transform: translateY(-5px) scale(1.08) rotate(3deg);
  }
`;

const adultPulse = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 15px 5px rgba(59, 130, 246, 0.2);
  }
`;

const seniorGlow = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 1;
    box-shadow: 0 0 10px rgba(236, 72, 153, 0.3);
  }
  50% {
    transform: scale(1.03);
    opacity: 0.95;
    box-shadow: 0 0 20px rgba(236, 72, 153, 0.5);
  }
`;

const childIconSpin = keyframes`
  0%, 100% {
    transform: rotate(0deg) scale(1);
  }
  25% {
    transform: rotate(-5deg) scale(1.1);
  }
  50% {
    transform: rotate(0deg) scale(1.15);
  }
  75% {
    transform: rotate(5deg) scale(1.1);
  }
`;

const teenIconBounce = keyframes`
  0%, 100% {
    transform: translateY(0) scale(1);
  }
  50% {
    transform: translateY(-6px) scale(1.1);
  }
`;

const adultIconPulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.08);
  }
`;

const seniorIconGlow = keyframes`
  0%, 100% {
    transform: scale(1);
    filter: brightness(1);
  }
  50% {
    transform: scale(1.05);
    filter: brightness(1.2);
  }
`;

const slowPulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.02);
    opacity: 0.98;
  }
`;

const GenderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  margin-top: 0.5rem;
`;

const GenderBox = styled.div`
  position: relative;
  padding: 1rem 1rem;
  border: 2px solid ${props => {
    if (props.selected) {
      return props.genderColor || '#667eea';
    }
    return '#e5e7eb';
  }};
  border-radius: 18px;
  background: ${props => props.selected
    ? `${props.genderColor || '#667eea'}0d`
    : '#ffffff'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 0.75rem;
  min-height: 70px;
  overflow: hidden;
  box-shadow: ${props => props.selected
    ? `0 4px 12px ${props.genderColor || '#667eea'}20, 0 2px 4px rgba(0, 0, 0, 0.06)`
    : '0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.1)'};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: ${props => props.genderColor || '#667eea'};
    opacity: ${props => props.selected ? 1 : 0};
    transition: opacity 0.2s ease;
    border-radius: 18px 0 0 18px;
  }

  &:hover {
    ${props => !props.disabled && css`
      border-color: ${props.genderColor || '#667eea'};
      transform: translateY(-2px);
      box-shadow: 0 6px 16px ${props.genderColor || '#667eea'}25, 0 2px 4px rgba(0, 0, 0, 0.08);
      background: ${props.selected
      ? `${props.genderColor || '#667eea'}14`
      : `${props.genderColor || '#667eea'}08`};
    `}
  }

  &:active {
    ${props => !props.disabled && css`
      transform: translateY(0);
    `}
  }

  ${props => props.selected && props.genderType === 'male' && css`
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25), 0 2px 4px rgba(0, 0, 0, 0.06);
  `}

  ${props => props.selected && props.genderType === 'female' && css`
    box-shadow: 0 4px 12px rgba(236, 72, 153, 0.25), 0 2px 4px rgba(0, 0, 0, 0.06);
  `}

  ${props => props.animate && !props.selected && css`
    animation: ${slideIn} 0.5s ease-out;
  `}
`;

const GenderIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: ${props => props.selected
    ? `linear-gradient(135deg, ${props.genderColor || '#667eea'}, ${props.genderColor === '#ec4899' ? '#db2777' : '#764ba2'})`
    : '#f3f4f6'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.selected ? 'white' : '#6b7280'};
  transition: all 0.2s ease;
  position: relative;
  flex-shrink: 0;
  box-shadow: ${props => props.selected
    ? `0 3px 10px ${props.genderColor || '#667eea'}30`
    : '0 1px 3px rgba(0, 0, 0, 0.05)'};

  ${props => props.selected && props.genderType === 'male' && css`
    animation: ${slowPulse} 2s ease-in-out infinite;
  `}

  ${props => props.selected && props.genderType === 'female' && css`
    animation: ${slowPulse} 2s ease-in-out infinite;
  `}

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: ${props => props.selected ? '90px' : '0'};
    height: ${props => props.selected ? '90px' : '0'};
    border-radius: 50%;
    background: ${props => props.genderColor || '#667eea'}15;
    transition: all 0.4s ease;
    z-index: -1;
  }

  svg {
    transition: transform 0.3s ease;
    width: 40px;
    height: 40px;
  }
  
  &:hover svg {
    transform: scale(1.1);
  }
`;

const GenderLabel = styled.span`
  font-size: 0.9375rem;
  font-weight: ${props => props.selected ? '600' : '500'};
  color: ${props => props.selected ? (props.genderColor || '#667eea') : '#334155'};
  text-transform: capitalize;
  transition: all 0.2s ease;
  letter-spacing: 0;
  flex: 1;
  line-height: 1.4;
`;

const GenderSubtext = styled.span`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  margin-top: -${props => props.theme.spacing.xs};
`;

const Checkmark = styled.div`
  display: none;
  z-index: 10;
  
  ${props => props.selected && css`
    animation: ${bounce} 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  `}
`;

const GenderAgeGroupRow = styled.div`
  display: grid;
  grid-template-columns: 0.6fr 1.4fr;
  gap: 2rem;
  margin-top: 0.75rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const GenderSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const AgeGroupSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const AgeGroupContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
  margin-top: 0.5rem;
  
  @media (max-width: 1024px) {
  grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const AgeGroupBox = styled.div`
  position: relative;
  padding: 0.75rem 0.5rem;
  border: 2px solid ${props => props.selected ? (props.genderColor || '#667eea') : '#e5e7eb'};
  border-radius: 18px;
  background: ${props => props.selected
    ? `${props.genderColor || '#667eea'}0d`
    : '#ffffff'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  min-height: 70px;
  box-shadow: ${props => props.selected
    ? `0 4px 12px ${props.genderColor || '#667eea'}33, 0 2px 4px rgba(0, 0, 0, 0.06)`
    : '0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.1)'};

  &:hover {
    ${props => !props.disabled && css`
      border-color: ${props.genderColor || '#667eea'};
      transform: translateY(-2px);
      box-shadow: 0 6px 16px ${props.genderColor || '#667eea'}40, 0 2px 4px rgba(0, 0, 0, 0.08);
      background: ${props.selected
      ? `${props.genderColor || '#667eea'}14`
      : `${props.genderColor || '#667eea'}08`};
    `}
  }

  &:active {
    ${props => !props.disabled && css`
      transform: translateY(0);
    `}
  }

  ${props => props.selected && css`
    box-shadow: 0 4px 12px ${props.genderColor || '#667eea'}33, 0 2px 4px rgba(0, 0, 0, 0.06);
  `}
`;

const AgeGroupIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${props => props.selected
    ? `linear-gradient(135deg, ${props.genderColor || '#667eea'}, ${props.genderColor === '#ec4899' ? '#db2777' : props.genderColor === '#3b82f6' ? '#2563eb' : '#764ba2'})`
    : '#f3f4f6'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.selected ? 'white' : '#6b7280'};
  transition: all 0.2s ease;
  box-shadow: ${props => props.selected
    ? `0 2px 8px ${props.genderColor || '#667eea'}40`
    : '0 1px 2px rgba(0, 0, 0, 0.05)'};
  flex-shrink: 0;
  margin-bottom: 0.5rem;
`;

const AgeGroupLabel = styled.span`
  font-size: 0.8125rem;
  font-weight: ${props => props.selected ? '600' : '500'};
  color: ${props => props.selected ? (props.genderColor || '#667eea') : '#334155'};
  text-align: center;
  transition: all 0.2s ease;
  line-height: 1.4;
  margin-top: -0.25rem;
`;

const AgeGroupSubtext = styled.span`
  font-size: 0.6875rem;
  color: #64748b;
  text-align: center;
  font-weight: 500;
  line-height: 1.2;
`;

const ChildAgeRangeContainer = styled.div`
  grid-template-columns: repeat(3, 1fr);
  gap: 0.375rem;
  margin-top: 0.375rem;
  padding: 0.375rem;
  background: #f9fafb;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  display: ${props => props.show ? 'grid' : 'none'};
  animation: ${props => props.show ? css`${slideIn} 0.3s ease-out` : 'none'};
`;

const ChildAgeRangeBox = styled.div`
  position: relative;
  padding: 0.5rem 0.375rem;
  border: 2px solid ${props => props.selected ? (props.genderColor || '#667eea') : '#e2e8f0'};
  border-radius: 8px;
  background: ${props => props.selected
    ? `linear-gradient(135deg, ${props.genderColor || '#667eea'}1f, ${props.genderColor || '#667eea'}0f)`
    : '#ffffff'};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  min-height: 50px;

  &:hover {
    border-color: ${props => props.genderColor || props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.sm};
    background: ${props => props.selected
    ? `linear-gradient(135deg, ${props.genderColor || props.theme.colors.primaryLight}30, ${props.genderColor || props.theme.colors.primaryLight}20)`
    : `${props.genderColor || props.theme.colors.primaryLight}10`};
  }

  &:active {
    transform: translateY(0) scale(0.98);
  }

  ${props => props.selected && css`
    animation: ${bounce} 0.3s ease-in-out;
    box-shadow: 0 2px 8px ${props.genderColor || props.theme.colors.primaryLight}40;
  `}
`;

const ChildAgeRangeLabel = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${props => props.selected ? (props.genderColor || '#667eea') : '#1e293b'};
  text-align: center;
  transition: color 0.3s ease;
  line-height: 1.2;
`;

const MotiveContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const MotiveBox = styled.div`
  position: relative;
  padding: 0.875rem 0.5rem;
  border: 2px solid ${props => props.selected ? (props.genderColor || '#667eea') : '#e5e7eb'};
  border-radius: 18px;
  background: ${props => props.selected
    ? `${props.genderColor || '#667eea'}0d`
    : '#ffffff'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 80px;
  box-shadow: ${props => props.selected
    ? `0 4px 12px ${props.genderColor || '#667eea'}33, 0 2px 4px rgba(0, 0, 0, 0.06)`
    : '0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.1)'};

  &:hover {
    ${props => !props.disabled && css`
      border-color: ${props.genderColor || '#667eea'};
      transform: translateY(-2px);
      box-shadow: 0 6px 16px ${props.genderColor || '#667eea'}40, 0 2px 4px rgba(0, 0, 0, 0.08);
      background: ${props.selected
      ? `${props.genderColor || '#667eea'}14`
      : `${props.genderColor || '#667eea'}08`};
    `}
  }

  &:active {
    ${props => !props.disabled && css`
      transform: translateY(0);
    `}
  }

  ${props => props.selected && css`
    box-shadow: 0 4px 12px ${props.genderColor || '#667eea'}33, 0 2px 4px rgba(0, 0, 0, 0.06);
  `}
`;

const MotiveIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.selected
    ? `linear-gradient(135deg, ${props.genderColor || '#667eea'}, ${props.genderColor === '#ec4899' ? '#db2777' : props.genderColor === '#3b82f6' ? '#2563eb' : '#764ba2'})`
    : '#f1f5f9'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.selected ? 'white' : '#94a3b8'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${props => props.selected
    ? `0 3px 8px ${props.genderColor || '#667eea'}40`
    : '0 2px 4px rgba(0, 0, 0, 0.05)'};
`;

const MotiveLabel = styled.span`
  font-size: 0.8125rem;
  font-weight: ${props => props.selected ? '600' : '500'};
  color: ${props => props.selected ? (props.genderColor || '#667eea') : '#334155'};
  text-align: center;
  transition: all 0.2s ease;
  letter-spacing: 0;
  line-height: 1.4;
`;

const EyeglassesTypeContainer = styled.div`
  grid-template-columns: repeat(3, 1fr);
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.md};
  background: ${props => props.theme.colors.gray50};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
  display: ${props => props.show ? 'grid' : 'none'};
  animation: ${props => props.show ? css`${slideIn} 0.3s ease-out` : 'none'};
`;

const EyeglassesTypeBox = styled.div`
  position: relative;
  padding: 0.5rem 0.375rem;
  border: 2px solid ${props => props.selected ? (props.genderColor || '#667eea') : '#e2e8f0'};
  border-radius: 10px;
  background: ${props => props.selected
    ? `linear-gradient(135deg, ${props.genderColor || '#667eea'}1f, ${props.genderColor || '#667eea'}0f)`
    : '#ffffff'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  min-height: 60px;

  &:hover {
    ${props => !props.disabled && css`
      border-color: ${props.genderColor || props.theme.colors.primary};
      transform: translateY(-2px);
      box-shadow: ${props.theme.shadows.sm};
      background: ${props.selected
      ? `linear-gradient(135deg, ${props.genderColor || props.theme.colors.primaryLight}30, ${props.genderColor || props.theme.colors.primaryLight}20)`
      : `${props.genderColor || props.theme.colors.primaryLight}10`};
    `}
  }

  &:active {
    ${props => !props.disabled && css`
      transform: translateY(0) scale(0.98);
    `}
  }

  ${props => props.selected && css`
    animation: ${bounce} 0.3s ease-in-out;
    box-shadow: 0 2px 8px ${props.genderColor || props.theme.colors.primaryLight}40;
  `}
`;

const EyeglassesTypeLabel = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${props => props.selected ? (props.genderColor || '#667eea') : '#1e293b'};
  text-align: center;
  transition: color 0.3s ease;
  line-height: 1.2;
`;

const InquiryTypeContainer = styled.div`
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
  margin-top: 0.5rem;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  display: ${props => props.show ? 'grid' : 'none'};
  animation: ${props => props.show ? css`${slideIn} 0.3s ease-out` : 'none'};
`;

const InquiryTypeBox = styled.div`
  position: relative;
  padding: 0.5rem 0.375rem;
  border: 2px solid ${props => props.selected ? (props.genderColor || '#667eea') : '#e2e8f0'};
  border-radius: 10px;
  background: ${props => props.selected
    ? `linear-gradient(135deg, ${props.genderColor || '#667eea'}1f, ${props.genderColor || '#667eea'}0f)`
    : '#ffffff'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  min-height: 60px;

  &:hover {
    ${props => !props.disabled && css`
      border-color: ${props.genderColor || props.theme.colors.primary};
      transform: translateY(-2px);
      box-shadow: ${props.theme.shadows.sm};
      background: ${props.selected
      ? `linear-gradient(135deg, ${props.genderColor || props.theme.colors.primaryLight}30, ${props.genderColor || props.theme.colors.primaryLight}20)`
      : `${props.genderColor || props.theme.colors.primaryLight}10`};
    `}
  }

  &:active {
    ${props => !props.disabled && css`
      transform: translateY(0) scale(0.98);
    `}
  }

  ${props => props.selected && css`
    animation: ${bounce} 0.3s ease-in-out;
    box-shadow: 0 2px 8px ${props.genderColor || props.theme.colors.primaryLight}40;
  `}
`;

const InquiryTypeLabel = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${props => props.selected ? (props.genderColor || '#667eea') : '#1e293b'};
  text-align: center;
  transition: color 0.3s ease;
  line-height: 1.2;
`;

const PreviousEyeglassesContainer = styled.div`
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
  margin-top: 0.5rem;
  display: ${props => props.show ? 'grid' : 'none'};
  animation: ${props => props.show ? css`${slideIn} 0.3s ease-out` : 'none'};
`;

const PreviousEyeglassesBox = styled.div`
  position: relative;
  padding: 0.5rem 0.375rem;
  border: 2px solid ${props => props.selected ? '#667eea' : '#e2e8f0'};
  border-radius: 10px;
  background: ${props => props.selected
    ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.12), rgba(102, 126, 234, 0.06))'
    : '#ffffff'};
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  min-height: 60px;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.sm};
    background: ${props => props.selected
    ? `linear-gradient(135deg, ${props.theme.colors.primaryLight}30, ${props.theme.colors.primaryLight}20)`
    : `${props.theme.colors.primaryLight}10`};
  }

  &:active {
    transform: translateY(0) scale(0.98);
  }

  ${props => props.selected && css`
    animation: ${bounce} 0.3s ease-in-out;
    box-shadow: 0 2px 8px ${props.theme.colors.primaryLight}30;
  `}
`;

const PreviousEyeglassesLabel = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${props => props.selected ? (props.genderColor || '#667eea') : '#1e293b'};
  text-align: center;
  transition: color 0.3s ease;
  line-height: 1.2;
`;

const PrescriptionStatusContainer = styled.div`
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
  margin-top: 0.5rem;
  display: ${props => props.show ? 'grid' : 'none'};
  animation: ${props => props.show ? css`${slideIn} 0.3s ease-out` : 'none'};
`;

const PrescriptionStatusBox = styled.div`
  position: relative;
  padding: 0.5rem 0.375rem;
  border: 2px solid ${props => props.selected ? (props.genderColor || '#667eea') : '#e2e8f0'};
  border-radius: 10px;
  background: ${props => props.selected
    ? `linear-gradient(135deg, ${props.genderColor || '#667eea'}1f, ${props.genderColor || '#667eea'}0f)`
    : '#ffffff'};
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  min-height: 60px;

  &:hover {
    border-color: ${props => props.genderColor || props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.sm};
    background: ${props => props.selected
    ? `linear-gradient(135deg, ${props.genderColor || props.theme.colors.primaryLight}30, ${props.genderColor || props.theme.colors.primaryLight}20)`
    : `${props.genderColor || props.theme.colors.primaryLight}10`};
  }

  &:active {
    transform: translateY(0) scale(0.98);
  }

  ${props => props.selected && css`
    animation: ${bounce} 0.3s ease-in-out;
    box-shadow: 0 2px 8px ${props.genderColor || props.theme.colors.primaryLight}40;
  `}
`;

const VisionTypeContainer = styled.div`
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  margin-top: 0.5rem;
  display: ${props => props.show ? 'grid' : 'none'};
  animation: ${props => props.show ? css`${slideIn} 0.3s ease-out` : 'none'};
`;

const VisionTypeBox = styled.div`
  position: relative;
  padding: 0.5rem 0.375rem;
  border: 2px solid ${props => props.selected ? (props.genderColor || '#667eea') : '#e2e8f0'};
  border-radius: 10px;
  background: ${props => props.selected
    ? `linear-gradient(135deg, ${props.genderColor || '#667eea'}1f, ${props.genderColor || '#667eea'}0f)`
    : '#ffffff'};
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  min-height: 60px;

  &:hover {
    border-color: ${props => props.genderColor || props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.sm};
    background: ${props => props.selected
    ? `linear-gradient(135deg, ${props.genderColor || props.theme.colors.primaryLight}30, ${props.genderColor || props.theme.colors.primaryLight}20)`
    : `${props.genderColor || props.theme.colors.primaryLight}10`};
  }

  &:active {
    transform: translateY(0) scale(0.98);
  }

  ${props => props.selected && css`
    animation: ${bounce} 0.3s ease-in-out;
    box-shadow: 0 2px 8px ${props.genderColor || props.theme.colors.primaryLight}40;
  `}
`;

const VisionTypeLabel = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${props => props.selected ? (props.genderColor || '#667eea') : '#1e293b'};
  text-align: center;
  transition: color 0.3s ease;
  line-height: 1.2;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.625rem 0.875rem;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.875rem;
  font-family: inherit;
  transition: border-color 0.2s ease;
  min-height: 80px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const EyeMeasurementGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const EyeSection = styled.div`
  background: #f9fafb;
  padding: 0.75rem;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
`;

const EyeTitle = styled.h4`
  font-size: 0.875rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 0.5rem 0;
  text-align: center;
`;

const MeasurementGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
`;

// Prescription-style table: OD / OG rows stacked, one column per measurement
//
// Data columns are a fixed 7rem — big enough that the two 1.8rem stepper
// buttons plus their gaps still leave room for the digits — instead of
// minmax(0, 1fr): on a tablet, 4 columns (progressive's Add column showing)
// used to squeeze each cell so far that the stepper buttons ate the whole
// width and the number itself became unreadable/invisible. A fixed size
// (rather than minmax(7rem, 1fr)) also stops columns ballooning huge with
// mostly empty space on a wide screen — below it the table scrolls
// horizontally (see RxTableScroll) instead of squeezing further.
const RxTable = styled.div`
  display: grid;
  grid-template-columns: 3rem repeat(${props => props.$cols}, 7rem);
  gap: 0.5rem;
  align-items: center;
  background: #f9fafb;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.75rem;
  min-width: max-content;

  @media (max-width: 640px) {
    grid-template-columns: 2.25rem repeat(${props => props.$cols}, 6rem);
    gap: 0.375rem;
  }
`;

// Lets the table scroll sideways instead of squeezing its columns unreadable
// when the viewport (tablet, sidebar open) is narrower than the content needs.
const RxTableScroll = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const RxHeadCell = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-align: center;
`;

const RxRowLabel = styled.div`
  font-size: 0.8125rem;
  font-weight: 700;
  color: #667eea;
`;

// One correction cell: a value flanked by its own -/+ buttons. Chrome renders
// no spinner arrows at all on touch devices, and the Android numeric keypad has
// no minus key, which left tablet users with no way to enter a negative sphere
// or cylinder. These buttons are the only stepper on every platform, so the
// native ones are switched off below to avoid showing two sets on desktop.
const RxFieldRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 0.25rem;

  input[type='number'] {
    flex: 1 1 auto;
    min-width: 0;
    text-align: center;
    padding-left: 0.3rem;
    padding-right: 0.3rem;
    font-size: 0.9rem;
    -moz-appearance: textfield;
  }
  input[type='number']::-webkit-outer-spin-button,
  input[type='number']::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  @media (max-width: 640px) {
    gap: 0.15rem;
    input[type='number'] { font-size: 0.85rem; padding-left: 0.15rem; padding-right: 0.15rem; }
  }
`;

// Sized for a fingertip, not a mouse pointer - this is the tablet's only stepper.
const RxStepButton = styled.button`
  flex: 0 0 auto;
  width: 1.8rem;
  padding: 0;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  color: #475569;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  touch-action: manipulation;
  user-select: none;
  -webkit-user-select: none;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover:not(:disabled) { background: #eef2ff; border-color: #c7d2fe; }
  &:active:not(:disabled) { background: #e0e7ff; border-color: #a5b4fc; }

  @media (max-width: 640px) {
    width: 1.5rem;
    font-size: 0.9rem;
  }
`;

const IncompleteOrdersSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin: 1.5rem auto;
  max-width: 1200px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  
  @media (max-width: 768px) {
    margin: 1rem;
    padding: 1rem;
  }
`;

const IncompleteOrdersHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const IncompleteOrdersTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  color: #64748b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const IncompleteOrdersToggleButton = styled.button`
  padding: 0.5rem 1rem;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #5568d3;
    transform: translateY(-1px);
  }
`;

const SearchInputGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.875rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 0.9375rem;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    }
`;

const SearchLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #475569;
  margin-bottom: 0.5rem;
  display: block;
`;

const SearchResults = styled.div`
  margin-top: 1rem;
  max-height: 500px;
  overflow-y: auto;
`;

const OrderResultCard = styled.div`
  background: #fef2f2;
  border: 2px solid #fecaca;
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const OrderResultTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: #991b1b;
  margin: 0 0 0.75rem 0;
`;

const OrderResultInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.75rem;
  font-size: 0.875rem;
  color: #64748b;
`;

const OrderResultItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  
  span:first-child {
    font-weight: 600;
    color: #475569;
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 2rem;
  color: #64748b;
  font-size: 0.9375rem;
`;

const CompleteOrderButton = styled.button`
  margin-top: 1rem;
  padding: 0.75rem 1.5rem;
  background: #059669;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  justify-content: center;
  
  &:hover {
    background: #047857;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(5, 150, 105, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const VisitorEntry = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [startTime] = useState(Date.now()); // Track when form was opened
  const [salesPerson, setSalesPerson] = useState(null);
  // Users without a fixed store (admins / managers running sales) must pick the
  // store they're selling at, like the store-manager picker.
  const [salesStore, setSalesStore] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('salesStore') || 'null'); } catch (_) { return null; }
  });
  const [salesStoreList, setSalesStoreList] = useState([]);
  // Incomplete orders search moved to separate SearchPage component

  useEffect(() => {
    // Get store info from localStorage
    const storedStoreInfo = localStorage.getItem('storeInfo');
    if (storedStoreInfo) {
      try {
        setStoreInfo(JSON.parse(storedStoreInfo));
      } catch (e) {
        console.error('Error parsing stored store info:', e);
      }
    }

    // Get sales person info from localStorage
    const userData = localStorage.getItem('user');
    const token = localStorage.getItem('token');

    if (!token || !userData) {
      // No token or user data, redirect to login
      navigate('/');
      return;
    }

    try {
      const user = JSON.parse(userData);
      // Sales persons and store managers run the sales flow; admins can too.
      const allowedSalesRoles = ['sales_person', 'store_manager', 'admin', 'super_admin'];
      if (allowedSalesRoles.includes(user.role)) {
        setSalesPerson(user);
      } else {
        // Not allowed to run the sales flow, redirect to login
        console.error('User role cannot run the sales flow:', user.role);
        navigate('/');
      }
    } catch (err) {
      console.error('Error parsing user data:', err);
      navigate('/');
    }
  }, [navigate]);

  // A user with no store of their own (admin / manager doing sales) must choose
  // which store this sale belongs to before starting.
  const ownStoreId = salesPerson?.store?.id || salesPerson?.store?._id || salesPerson?.storeId || null;
  const needsStorePicker = !!salesPerson && !ownStoreId;

  useEffect(() => {
    if (!needsStorePicker) return;
    const token = localStorage.getItem('token');
    axios.get('/api/stores', { headers: token ? { Authorization: `Bearer ${token}` } : undefined })
      .then(({ data }) => setSalesStoreList((data?.stores || []).filter((s) => !s.isCentralWarehouse && !s.is_central_warehouse)))
      .catch(() => {});
  }, [needsStorePicker]);

  const handlePickSalesStore = (store) => {
    setSalesStore(store || null);
    if (store) sessionStorage.setItem('salesStore', JSON.stringify(store));
    else sessionStorage.removeItem('salesStore');
  };

  // Incomplete orders functionality moved to separate pages

  // Logout is now handled by SalesLayout

  // Tracking order functionality removed - use Track Order page in sidebar instead

  const visitorFormDefaults = {
    gender: '',
    ageGroup: '',
    childAgeRange: '',
    hasCompanion: false,
    companionRelationship: '',
    companionCount: '',
    companionAge: '',
    companionWearsGlasses: false,
    companionWearingCount: '',
    motive: '',
    eyeglassesType: '',
    inquiryType: '',
    hadPreviousEyeglasses: null,
    previousPrescription: '',
    // New fields for prescription flow
    prescriptionStatus: null, // 'new' or 'already_wear'
    visionType: '', // 'simple_vision', 'progressive', 'other'
    // Simple vision fields
    simpleVisionCorrectionType: '', // 'far', 'closeup', 'both'
    // Far correction - separate fields for each eye
    simpleVisionFarRightEye: {
      sphere: '',
      cylinder: '',
      axis: '',
      add: ''
    },
    simpleVisionFarLeftEye: {
      sphere: '',
      cylinder: '',
      axis: '',
      add: ''
    },
    // Close Up correction - separate fields for each eye
    simpleVisionCloseUpRightEye: {
      sphere: '',
      cylinder: '',
      axis: '',
      add: ''
    },
    simpleVisionCloseUpLeftEye: {
      sphere: '',
      cylinder: '',
      axis: '',
      add: ''
    },
    // Progressive fields
    progressiveType: '',
    progressiveOldCorrection: '',
    // Structured OD/OS/Add old correction for progressive (replaces the free-text box).
    progressiveOldRightEye: { sphere: '', cylinder: '', axis: '', add: '' },
    progressiveOldLeftEye: { sphere: '', cylinder: '', axis: '', add: '' },
    progressiveImpression: '',
    verifyOldGlasses: false,
    // Contact Lenses sub-type (shown when visionType === 'other').
    // Field name is kept for back-compat with existing visit records.
    otherContactLensType: '' // 'soft' | 'hard' | 'therapeutic'
  };

  // Restore the visit form when returning via the progress bar or after a
  // page refresh. A fresh "New Visit" / "New Client" clears this key first,
  // so a brand-new visit always starts empty.
  const [formData, setFormData] = useState(() => {
    try {
      const saved = sessionStorage.getItem('visitorFormData');
      if (saved) return { ...visitorFormDefaults, ...JSON.parse(saved) };
    } catch (e) {
      console.error('Error restoring visitor form data:', e);
    }
    return visitorFormDefaults;
  });

  const [genderAnimate, setGenderAnimate] = useState(false);
  const [ageGroupAnimate, setAgeGroupAnimate] = useState(false);
  const [storeInfo, setStoreInfo] = useState(null);

  // Persist the visit form on every change so it survives back-navigation
  // (progress bar) and refreshes.
  useEffect(() => {
    sessionStorage.setItem('visitorFormData', JSON.stringify(formData));
  }, [formData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle nested fields (e.g., simpleVisionFarRightEye.sphere)
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => {
        const newData = {
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: type === 'checkbox' ? checked : value
          }
        };
        // Clear companionCount if relationship is not parent or friend
        if (name === 'companionRelationship' && value !== 'parent' && value !== 'friend') {
          newData.companionCount = '';
        }
        return newData;
      });
    } else {
      setFormData(prev => {
        const newData = {
          ...prev,
          [name]: type === 'checkbox' ? checked : value
        };
        // Clear companionCount if relationship is not parent or friend
        if (name === 'companionRelationship' && value !== 'parent' && value !== 'friend') {
          newData.companionCount = '';
        }
        return newData;
      });
    }
  };

  const handleGenderSelect = (gender) => {
    setGenderAnimate(true);
    setFormData(prev => {
      const isDeselecting = prev.gender === gender;
      return {
        ...prev,
        gender: isDeselecting ? '' : gender,
        // Clear all dependent fields when deselecting gender
        ...(isDeselecting && {
          ageGroup: '',
          childAgeRange: '',
          motive: '',
          eyeglassesType: '',
          inquiryType: '',
          hadPreviousEyeglasses: null,
          previousPrescription: '',
          prescriptionStatus: null,
          visionType: '',
          simpleVisionCorrectionType: '',
          simpleVisionFarRightEye: { sphere: '', cylinder: '', axis: '', add: '' },
          simpleVisionFarLeftEye: { sphere: '', cylinder: '', axis: '', add: '' },
          simpleVisionCloseUpRightEye: { sphere: '', cylinder: '', axis: '', add: '' },
          simpleVisionCloseUpLeftEye: { sphere: '', cylinder: '', axis: '', add: '' },
          progressiveType: '',
          progressiveOldCorrection: '',
          progressiveImpression: '',
          verifyOldGlasses: false,
          otherContactLensType: ''
        })
      };
    });
    setTimeout(() => setGenderAnimate(false), 600);
  };

  const handleAgeGroupSelect = (ageGroup) => {
    if (!formData.gender) return; // Disabled if gender not selected
    setAgeGroupAnimate(true);
    setFormData(prev => {
      const isDeselecting = prev.ageGroup === ageGroup;
      const newAgeGroup = isDeselecting ? '' : ageGroup;
      return {
        ...prev,
        ageGroup: newAgeGroup,
        childAgeRange: newAgeGroup !== 'child' ? '' : prev.childAgeRange, // Clear if not child
        // Clear all dependent fields when deselecting age group
        ...(isDeselecting && {
          motive: '',
          eyeglassesType: '',
          inquiryType: '',
          hadPreviousEyeglasses: null,
          previousPrescription: '',
          prescriptionStatus: null,
          visionType: '',
          simpleVisionCorrectionType: '',
          simpleVisionFarRightEye: { sphere: '', cylinder: '', axis: '', add: '' },
          simpleVisionFarLeftEye: { sphere: '', cylinder: '', axis: '', add: '' },
          simpleVisionCloseUpRightEye: { sphere: '', cylinder: '', axis: '', add: '' },
          simpleVisionCloseUpLeftEye: { sphere: '', cylinder: '', axis: '', add: '' },
          progressiveType: '',
          progressiveOldCorrection: '',
          progressiveImpression: '',
          verifyOldGlasses: false,
          otherContactLensType: ''
        }),
        // Clear progressive selection if changing from senior to non-senior
        ...(!isDeselecting && prev.ageGroup === 'senior' && newAgeGroup !== 'senior' && {
          visionType: prev.visionType === 'progressive' ? '' : prev.visionType,
          progressiveType: '',
          progressiveOldCorrection: '',
          progressiveImpression: '',
          // Non-seniors only use Far correction (selector is hidden); force Far and drop Close Up
          simpleVisionCorrectionType: prev.visionType === 'simple_vision' ? 'far' : prev.simpleVisionCorrectionType,
          simpleVisionCloseUpRightEye: { sphere: '', cylinder: '', axis: '', add: '' },
          simpleVisionCloseUpLeftEye: { sphere: '', cylinder: '', axis: '', add: '' }
        })
      };
    });
    setTimeout(() => setAgeGroupAnimate(false), 600);
  };

  const handleChildAgeRangeSelect = (childAgeRange) => {
    if (!formData.ageGroup || formData.ageGroup !== 'child') return; // Disabled if age group not child
    setFormData(prev => ({
      ...prev,
      childAgeRange: prev.childAgeRange === childAgeRange ? '' : childAgeRange
    }));
  };

  const handleMotiveSelect = (motive) => {
    if (!formData.ageGroup) return; // Disabled if age group not selected

    const isDeselecting = formData.motive === motive;

    // If child is selected, require child age range (only when selecting, not deselecting)
    if (!isDeselecting && formData.ageGroup === 'child' && !formData.childAgeRange) {
      alert(t('visitor.alertChildRange'));
      return;
    }

    setFormData(prev => {
      const newMotive = isDeselecting ? '' : motive;
      return {
        ...prev,
        motive: newMotive,
        eyeglassesType: newMotive !== 'buying_eyeglasses' ? '' : prev.eyeglassesType,
        inquiryType: newMotive !== 'inquiry' ? '' : prev.inquiryType,
        // Clear all dependent fields when deselecting motive
        ...(isDeselecting && {
          hadPreviousEyeglasses: null,
          previousPrescription: '',
          prescriptionStatus: null,
          visionType: '',
          simpleVisionCorrectionType: '',
          simpleVisionFarRightEye: { sphere: '', cylinder: '', axis: '', add: '' },
          simpleVisionFarLeftEye: { sphere: '', cylinder: '', axis: '', add: '' },
          simpleVisionCloseUpRightEye: { sphere: '', cylinder: '', axis: '', add: '' },
          simpleVisionCloseUpLeftEye: { sphere: '', cylinder: '', axis: '', add: '' },
          progressiveType: '',
          progressiveOldCorrection: '',
          progressiveImpression: '',
          verifyOldGlasses: false,
          otherContactLensType: ''
        })
      };
    });
    // Don't navigate immediately - wait for validate button click
  };

  const clearDownstreamSessionData = () => {
    const keysToRemove = [
      'prescriptionId',
      'orderData',
      'orderId',
      'orderNumber',
      'selectedFrame',
      'measurementData',
      'orderSummary',
      'paymentData',
      'measurementFormData',
      'prescriptionFormData',
      'existingPrescription',
      'frameSelectionData',
      'paymentFormData',
      'prescriptionPhotoState',
      'prescriptionOrderState',
      'hasPrescriptionPhoto'
    ];
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
  };

  const handleSaveAndNavigate = async (motive, additionalData = {}) => {
    // When a companion is present, force a relationship choice.
    if (formData.hasCompanion && !formData.companionRelationship) {
      alert(t('visitor.chooseRelationship', 'Please choose the companion relationship.'));
      return;
    }
    try {
      setLoading(true);

      // Calculate time taken in seconds
      const endTime = Date.now();
      const timeTaken = Math.round((endTime - startTime) / 1000);

      // Get sales person ID from state
      const salesPersonId = salesPerson?.id || null;

      // Prepare data to send with latest formData and any additional data
      const dataToSend = {
        ...formData,
        ...additionalData,
        motive,
        timeTaken,
        salesPerson: salesPersonId,
        // Attribute the sale to the chosen store (own store for regular sales staff)
        store: ownStoreId || salesStore?.id || undefined
      };

      // Remove navigateTo from dataToSend before saving (it's only for navigation)
      const { navigateTo, ...dataToSave } = dataToSend;

      // Save visitor data to backend. If this visit already exists, update it
      // so downstream RX/frame/payment state stays linked to the same visit.
      const token = localStorage.getItem('token');
      const existingVisitorId = sessionStorage.getItem('visitorId');
      const requestConfig = {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      };
      const response = existingVisitorId
        ? await axios.put(`/api/visitors/${existingVisitorId}`, dataToSave, requestConfig)
        : await axios.post('/api/visitors', dataToSave, requestConfig);
      const visitorId = response.data._id || response.data.id || existingVisitorId;

      if (!existingVisitorId) {
        clearDownstreamSessionData();
      }

      // Store visitor ID and data in session storage
      sessionStorage.setItem('visitorId', visitorId);
      sessionStorage.setItem('visitorData', JSON.stringify(dataToSave));

      // Navigate to the specified route or default to prescription
      navigate(navigateTo || '/prescription');
    } catch (error) {
      console.error('Error saving visitor:', error);
      // Still proceed with navigation even if save fails
      const { navigateTo, ...dataToSave } = additionalData;

      if (!sessionStorage.getItem('visitorId')) {
        clearDownstreamSessionData();
      }

      sessionStorage.setItem('visitorData', JSON.stringify({
        ...formData,
        ...dataToSave,
        motive
      }));
      navigate(additionalData.navigateTo || '/prescription');
    } finally {
      setLoading(false);
    }
  };

  const handleEyeglassesTypeSelect = (eyeglassesType) => {
    if (!formData.motive || formData.motive !== 'buying_eyeglasses') return;

    setFormData(prev => {
      const newEyeglassesType = prev.eyeglassesType === eyeglassesType ? '' : eyeglassesType;
      return {
        ...prev,
        eyeglassesType: newEyeglassesType,
        // Clear previous eyeglasses data if switching away from prescription
        hadPreviousEyeglasses: newEyeglassesType === 'prescription' ? prev.hadPreviousEyeglasses : null,
        previousPrescription: newEyeglassesType === 'prescription' ? prev.previousPrescription : '',
        // Clear prescription status and vision type if switching away from prescription
        prescriptionStatus: newEyeglassesType === 'prescription' ? prev.prescriptionStatus : null,
        visionType: newEyeglassesType === 'prescription' ? prev.visionType : '',
        // Clear all vision type specific fields
        simpleVisionCorrectionType: '',
        simpleVisionFarRightEye: { sphere: '', cylinder: '', axis: '', add: '' },
        simpleVisionFarLeftEye: { sphere: '', cylinder: '', axis: '', add: '' },
        simpleVisionCloseUpRightEye: { sphere: '', cylinder: '', axis: '', add: '' },
        simpleVisionCloseUpLeftEye: { sphere: '', cylinder: '', axis: '', add: '' },
        progressiveType: '',
        progressiveOldCorrection: '',
        progressiveImpression: '',
        verifyOldGlasses: false,
        otherContactLensType: ''
      };
    });
    // Don't navigate immediately - wait for validate button click
  };

  const handleValidateAndNavigate = () => {
    const motive = formData.motive;

    // Users without a fixed store must pick which store this sale is for.
    if (needsStorePicker && !salesStore?.id) {
      alert(t('visitor.selectStoreFirst') || 'Please choose which store this sale is for.');
      return;
    }

    // If child is selected, require child age range
    if (formData.ageGroup === 'child' && !formData.childAgeRange) {
      alert(t('visitor.alertChildRange'));
      return;
    }

    // Validate based on motive
    if (motive === 'buying_eyeglasses' && !formData.eyeglassesType) {
      alert(t('visitor.alertEyeglassesType'));
      return;
    }

    // Validate prescription status if prescription is selected
    if (motive === 'buying_eyeglasses' && formData.eyeglassesType === 'prescription') {
      if (formData.prescriptionStatus === null) {
        alert(t('visitor.alertPrescriptionStatus'));
        return;
      }

      // If "Already Wear" is selected, validate vision type and required fields
      if (formData.prescriptionStatus === 'already_wear') {
        if (!formData.visionType) {
          alert(t('visitor.alertVisionType'));
          return;
        }

        // Validate Simple Vision fields
        if (formData.visionType === 'simple_vision') {
          if (!formData.simpleVisionCorrectionType) {
            alert(t('visitor.alertCorrectionType'));
            return;
          }
          // Measurement inputs (Far / Close Up) are optional for all age groups
        }

        // Validate Progressive fields
        if (formData.visionType === 'progressive') {
          if (!formData.progressiveType.trim()) {
            alert(t('visitor.alertProgressiveType'));
            return;
          }
          // Old correction is now a structured OD/OS/Add table — require at least
          // one eye's sphere to be entered.
          const hasOldCorrection = String(formData.progressiveOldRightEye?.sphere || '').trim() !== '' ||
            String(formData.progressiveOldLeftEye?.sphere || '').trim() !== '';
          if (!hasOldCorrection) {
            alert(t('visitor.alertProgressiveOld'));
            return;
          }
          if (!formData.progressiveImpression.trim()) {
            alert(t('visitor.alertImpression'));
            return;
          }
        }

        // Validate Other fields
        if (formData.visionType === 'other') {
          if (!formData.otherContactLensType) {
            alert(t('visitor.alertContactType'));
            return;
          }
        }
      }
    }

    // Navigate based on motive and type
    if (motive === 'buying_eyeglasses' && formData.eyeglassesType === 'prescription') {
      // Buying with prescription → go to order/prescription page
      handleSaveAndNavigate(motive, { navigateTo: '/prescription' });
    } else if (motive === 'buying_eyeglasses' && formData.eyeglassesType === 'no_prescription') {
      // Buying without prescription → go to quote page
      handleSaveAndNavigate(motive, { navigateTo: '/quote' });
    } else if (motive === 'inquiry') {
      // Inquiry → go to inquiry page
      handleSaveAndNavigate(motive, { navigateTo: '/inquiry' });
    } else if (motive === 'other_otc_sales') {
      // Other over-the-counter sales → go to quote page
      handleSaveAndNavigate(motive, { navigateTo: '/quote' });
    } else if (motive === 'solar' || motive === 'buying_eyeglasses') {
      // Frame & Solar / Buying Eyeglasses → go to prescription page.
      handleSaveAndNavigate(motive, { navigateTo: '/prescription' });
    }
  };

  const handleInquiryTypeSelect = (inquiryType) => {
    if (!formData.motive || formData.motive !== 'inquiry') return;
    setFormData(prev => ({
      ...prev,
      inquiryType
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Calculate time taken in seconds
      const endTime = Date.now();
      const timeTaken = Math.round((endTime - startTime) / 1000); // Convert to seconds

      // Get sales person ID from state
      const salesPersonId = salesPerson?.id || null;

      // Prepare data to send (but don't save to database yet)
      const dataToSend = {
        ...formData,
        timeTaken,
        salesPerson: salesPersonId
      };

      if (!sessionStorage.getItem('visitorId')) {
        clearDownstreamSessionData();
      }

      // Store visitor data in session storage only (will be saved when prescription is entered)
      sessionStorage.setItem('visitorData', JSON.stringify(dataToSend));
      sessionStorage.setItem('visitorTimeTaken', timeTaken.toString());

      // Navigate to prescription form
      navigate('/prescription');
    } catch (error) {
      console.error('Error preparing visitor data:', error);

      if (!sessionStorage.getItem('visitorId')) {
        clearDownstreamSessionData();
      }

      // Still proceed with navigation
      sessionStorage.setItem('visitorData', JSON.stringify(formData));
      navigate('/prescription');
    } finally {
      setLoading(false);
    }
  };

  const isChild = formData.ageGroup === 'child';
  const canProceed = formData.gender && formData.ageGroup;

  // Timers for press-and-hold on the correction -/+ buttons. One press at a
  // time, so a single ref covers every field.
  const rxHoldRef = useRef({ timeout: null, interval: null });
  useEffect(() => () => {
    clearTimeout(rxHoldRef.current.timeout);
    clearInterval(rxHoldRef.current.interval);
  }, []);

  // Renders an OD/OG correction table (rows stacked) for a pair of eye field groups.
  const renderCorrectionTable = (rightKey, leftKey, showAdd) => {
    const cols = showAdd ? 4 : 3;
    // Scrolling over a focused number input silently changes its value — blur to prevent it
    const preventWheel = (e) => e.currentTarget.blur();
    // Same steps and limits the prescription form enforces, so a value stepped
    // here is one that form would also accept.
    const rxStep = (field) => (field === 'axis' ? 1 : 0.25);
    const rxBounds = (field) => (
      field === 'sphere' ? { min: -20, max: 20 }
        : field === 'cylinder' ? { min: -6, max: 6 }
        : field === 'axis' ? { min: 0, max: 180 }
        : { min: 0, max: 4 }
    );
    // An empty field steps from 0, so the first tap on "-" gives -0.25.
    const applyStep = (key, field, direction) => {
      const step = rxStep(field);
      const { min, max } = rxBounds(field);
      setFormData(prev => {
        const current = parseFloat(prev[key][field]);
        const base = Number.isFinite(current) ? current : 0;
        const stepped = Math.round((base + direction * step) / step) * step;
        const next = Math.min(max, Math.max(min, stepped));
        const text = field === 'axis' ? String(next) : next.toFixed(2);
        const updated = { ...prev, [key]: { ...prev[key], [field]: text } };
        // Add mirrors OD into OG, same as typing it does.
        if (field === 'add' && key === rightKey) {
          updated[leftKey] = { ...prev[leftKey], add: text };
        }
        return updated;
      });
    };
    const stopHold = () => {
      clearTimeout(rxHoldRef.current.timeout);
      clearInterval(rxHoldRef.current.interval);
      rxHoldRef.current = { timeout: null, interval: null };
    };
    // Holding runs the step repeatedly - reaching -3.00 by tapping 12 times is
    // not a realistic way to enter a prescription on a tablet.
    const startHold = (e, key, field, direction) => {
      if (e.button !== undefined && e.button !== 0) return;
      e.preventDefault(); // keep focus, and the on-screen keyboard, where it is
      stopHold();
      applyStep(key, field, direction);
      rxHoldRef.current.timeout = setTimeout(() => {
        rxHoldRef.current.interval = setInterval(
          () => applyStep(key, field, direction), 80
        );
      }, 500);
    };
    const stepButton = (key, field, direction) => (
      <RxStepButton
        type="button"
        tabIndex={-1}
        aria-label={t(direction < 0 ? 'rx.decrease' : 'rx.increase', { field: t(`rx.${field}`) })}
        onPointerDown={(e) => startHold(e, key, field, direction)}
        onPointerUp={stopHold}
        onPointerLeave={stopHold}
        onPointerCancel={stopHold}
        onContextMenu={(e) => e.preventDefault()}
      >
        {direction < 0 ? '\u2212' : '+'}
      </RxStepButton>
    );
    const withSteppers = (key, field, input) => (
      <RxFieldRow>
        {stepButton(key, field, -1)}
        {input}
        {stepButton(key, field, 1)}
      </RxFieldRow>
    );
    const renderRow = (label, key) => {
      const isRight = key === rightKey;
      return (
      <>
        <RxRowLabel>{label}</RxRowLabel>
        {withSteppers(key, 'sphere', (
          <Input
            type="number"
            inputMode="decimal"
            step="0.25"
            name={`${key}.sphere`}
            value={formData[key].sphere}
            onChange={handleChange}
            onWheel={preventWheel}
            placeholder="0.00"
          />
        ))}
        {withSteppers(key, 'cylinder', (
          <Input
            type="number"
            inputMode="decimal"
            step="0.25"
            name={`${key}.cylinder`}
            value={formData[key].cylinder}
            onChange={handleChange}
            onWheel={preventWheel}
            placeholder="0.00"
          />
        ))}
        {withSteppers(key, 'axis', (
          <Input
            type="number"
            inputMode="decimal"
            name={`${key}.axis`}
            value={formData[key].axis}
            onChange={handleChange}
            onWheel={preventWheel}
            placeholder="0"
          />
        ))}
        {showAdd && withSteppers(key, 'add', (
          <Input
            type="number"
            inputMode="decimal"
            step="0.25"
            min="0"
            name={`${key}.add`}
            value={formData[key].add}
            onChange={isRight
              ? (e) => {
                const v = e.target.value;
                setFormData(prev => ({
                  ...prev,
                  [rightKey]: { ...prev[rightKey], add: v },
                  [leftKey]: { ...prev[leftKey], add: v }
                }));
              }
              : handleChange}
            onKeyDown={(e) => { if (e.key === '-' || e.key === 'Subtract') e.preventDefault(); }}
            onWheel={preventWheel}
            placeholder="0.00"
          />
        ))}
      </>
      );
    };
    return (
      <RxTableScroll>
        <RxTable $cols={cols}>
          <RxHeadCell />
          <RxHeadCell>{t('rx.sphere')}</RxHeadCell>
          <RxHeadCell>{t('rx.cylinder')}</RxHeadCell>
          <RxHeadCell>{t('rx.axis')}</RxHeadCell>
          {showAdd && <RxHeadCell>{t('rx.add')}</RxHeadCell>}
          {renderRow('OD', rightKey)}
          {renderRow('OG', leftKey)}
        </RxTable>
      </RxTableScroll>
    );
  };

  if (!salesPerson) {
    return null; // Wait for user data to load
  }

  return (
    <SalesLayout pageTitle={t('visitor.newVisitor')}>
      <Container>
        <ContentWrapper>
          <Card>
            <Form onSubmit={handleSubmit}>
              {needsStorePicker && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem', padding: '0.85rem 1rem', background: salesStore ? '#ecfdf5' : '#fef3c7', border: `1px solid ${salesStore ? '#a7f3d0' : '#fcd34d'}`, borderRadius: '12px' }}>
                  <Label style={{ margin: 0, fontWeight: 600, color: salesStore ? '#065f46' : '#92400e' }}>
                    {salesStore ? `${t('visitor.sellingAtStore') || 'Selling at store'}:` : `${t('visitor.selectStore') || 'Which store is this sale for'} *`}
                  </Label>
                  <select
                    value={salesStore?.id || ''}
                    onChange={(e) => {
                      const s = salesStoreList.find((st) => String(st.id || st._id) === e.target.value);
                      handlePickSalesStore(s ? { id: s.id || s._id, name: s.name } : null);
                    }}
                    style={{ padding: '0.5rem 0.7rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', background: '#fff', color: '#1e293b', minWidth: '200px' }}
                  >
                    <option value="">— {t('visitor.chooseStore') || 'choose a store'} —</option>
                    {salesStoreList.map((s) => (
                      <option key={s.id || s._id} value={s.id || s._id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <FormGroup>
                <GenderAgeGroupRow>
                  <GenderSection>
                    <Label>{t('visitor.gender')} *</Label>
                    <GenderContainer>
                      <GenderBox
                        selected={formData.gender === 'male'}
                        animate={genderAnimate && formData.gender !== 'male'}
                        onClick={() => handleGenderSelect('male')}
                        genderColor="#3b82f6"
                        genderType={formData.gender === 'male' ? 'male' : null}
                      >
                        <Checkmark selected={formData.gender === 'male'}>
                          <UserCheck size={16} />
                        </Checkmark>
                        <GenderIcon
                          selected={formData.gender === 'male'}
                          genderType={formData.gender === 'male' ? 'male' : null}
                          genderColor="#3b82f6"
                          style={{
                            background: formData.gender === 'male'
                              ? 'linear-gradient(135deg, #3b82f6, #2563eb)'
                              : undefined
                          }}
                        >
                          <IconImage
                            src="/icons/male.png"
                            alt={t('visitor.male')}
                            size="80px"
                            selected={formData.gender === 'male'}
                          />
                        </GenderIcon>
                        <GenderLabel
                          selected={formData.gender === 'male'}
                          style={{
                            color: formData.gender === 'male' ? '#3b82f6' : undefined
                          }}
                        >
                          {t('visitor.male')}
                        </GenderLabel>
                      </GenderBox>

                      <GenderBox
                        selected={formData.gender === 'female'}
                        animate={genderAnimate && formData.gender !== 'female'}
                        onClick={() => handleGenderSelect('female')}
                        genderColor="#ec4899"
                        genderType={formData.gender === 'female' ? 'female' : null}
                      >
                        <Checkmark
                          selected={formData.gender === 'female'}
                          style={{
                            background: formData.gender === 'female' ? '#ec4899' : undefined
                          }}
                        >
                          <UserCheck size={16} />
                        </Checkmark>
                        <GenderIcon
                          selected={formData.gender === 'female'}
                          genderType={formData.gender === 'female' ? 'female' : null}
                          genderColor="#ec4899"
                          style={{
                            background: formData.gender === 'female'
                              ? 'linear-gradient(135deg, #ec4899, #db2777)'
                              : undefined
                          }}
                        >
                          <IconImage
                            src="/icons/female.png"
                            alt={t('visitor.female')}
                            size="80px"
                            selected={formData.gender === 'female'}
                          />
                        </GenderIcon>
                        <GenderLabel
                          selected={formData.gender === 'female'}
                          style={{
                            color: formData.gender === 'female' ? '#ec4899' : undefined
                          }}
                        >
                          {t('visitor.female')}
                        </GenderLabel>
                      </GenderBox>
                    </GenderContainer>
                    <input
                      type="hidden"
                      name="gender"
                      value={formData.gender}
                      required
                    />
                  </GenderSection>

                  <AgeGroupSection>
                    <Label>{t('visitor.ageGroup')} *</Label>
                    <AgeGroupContainer>
                      <AgeGroupBox
                        selected={formData.ageGroup === 'child'}
                        animate={ageGroupAnimate && formData.ageGroup !== 'child'}
                        onClick={() => handleAgeGroupSelect('child')}
                        ageGroupType={formData.ageGroup === 'child' ? 'child' : null}
                        disabled={!formData.gender}
                        genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                      >
                        <Checkmark selected={formData.ageGroup === 'child'}>
                          <UserCheck size={14} />
                        </Checkmark>
                        <AgeGroupIcon
                          selected={formData.ageGroup === 'child'}
                          ageGroupType={formData.ageGroup === 'child' ? 'child' : null}
                          genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                        >
                          <IconImage
                            src="/icons/child.png"
                            alt={t('visitor.child')}
                            size="58px"
                            selected={formData.ageGroup === 'child'}
                          />
                        </AgeGroupIcon>
                        <AgeGroupLabel
                          selected={formData.ageGroup === 'child'}
                          genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                        >
                          {t('visitor.child')}
                        </AgeGroupLabel>
                        <AgeGroupSubtext>{t('visitor.range0_17')}</AgeGroupSubtext>
                      </AgeGroupBox>

                      <AgeGroupBox
                        selected={formData.ageGroup === 'young_adult'}
                        animate={ageGroupAnimate && formData.ageGroup !== 'young_adult'}
                        onClick={() => handleAgeGroupSelect('young_adult')}
                        ageGroupType={formData.ageGroup === 'young_adult' ? 'teen' : null}
                        disabled={!formData.gender}
                        genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                      >
                        <Checkmark selected={formData.ageGroup === 'young_adult'}>
                          <UserCheck size={12} />
                        </Checkmark>
                        <AgeGroupIcon
                          selected={formData.ageGroup === 'young_adult'}
                          ageGroupType={formData.ageGroup === 'young_adult' ? 'teen' : null}
                          genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                        >
                          <IconImage
                            src="/icons/teen.png"
                            alt={t('visitor.teen')}
                            size="58px"
                            selected={formData.ageGroup === 'young_adult'}
                          />
                        </AgeGroupIcon>
                        <AgeGroupLabel
                          selected={formData.ageGroup === 'young_adult'}
                          genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                        >
                          {t('visitor.teen')}
                        </AgeGroupLabel>
                        <AgeGroupSubtext>{t('visitor.range18_25')}</AgeGroupSubtext>
                      </AgeGroupBox>

                      <AgeGroupBox
                        selected={formData.ageGroup === 'adult'}
                        animate={ageGroupAnimate && formData.ageGroup !== 'adult'}
                        onClick={() => handleAgeGroupSelect('adult')}
                        ageGroupType={formData.ageGroup === 'adult' ? 'adult' : null}
                        disabled={!formData.gender}
                        genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                      >
                        <Checkmark selected={formData.ageGroup === 'adult'}>
                          <UserCheck size={12} />
                        </Checkmark>
                        <AgeGroupIcon
                          selected={formData.ageGroup === 'adult'}
                          ageGroupType={formData.ageGroup === 'adult' ? 'adult' : null}
                          genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                        >
                          <IconImage
                            src="/icons/adult.png"
                            alt={t('visitor.adult')}
                            size="58px"
                            selected={formData.ageGroup === 'adult'}
                          />
                        </AgeGroupIcon>
                        <AgeGroupLabel
                          selected={formData.ageGroup === 'adult'}
                          genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                        >
                          {t('visitor.adult')}
                        </AgeGroupLabel>
                        <AgeGroupSubtext>{t('visitor.range26_42')}</AgeGroupSubtext>
                      </AgeGroupBox>

                      <AgeGroupBox
                        selected={formData.ageGroup === 'senior'}
                        animate={ageGroupAnimate && formData.ageGroup !== 'senior'}
                        onClick={() => handleAgeGroupSelect('senior')}
                        ageGroupType={formData.ageGroup === 'senior' ? 'senior' : null}
                        disabled={!formData.gender}
                        genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                      >
                        <Checkmark selected={formData.ageGroup === 'senior'}>
                          <UserCheck size={12} />
                        </Checkmark>
                        <AgeGroupIcon
                          selected={formData.ageGroup === 'senior'}
                          ageGroupType={formData.ageGroup === 'senior' ? 'senior' : null}
                          genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                        >
                          <IconImage
                            src="/icons/senior.png"
                            alt={t('visitor.senior')}
                            size="58px"
                            selected={formData.ageGroup === 'senior'}
                          />
                        </AgeGroupIcon>
                        <AgeGroupLabel
                          selected={formData.ageGroup === 'senior'}
                          genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                        >
                          {t('visitor.senior')}
                        </AgeGroupLabel>
                        <AgeGroupSubtext>{t('visitor.range43plus')}</AgeGroupSubtext>
                      </AgeGroupBox>
                    </AgeGroupContainer>
                    <ChildAgeRangeContainer show={formData.ageGroup === 'child'}>
                      <ChildAgeRangeBox
                        selected={formData.childAgeRange === '0-6'}
                        onClick={() => handleChildAgeRangeSelect('0-6')}
                        disabled={!formData.ageGroup || formData.ageGroup !== 'child'}
                        genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                      >
                        <Checkmark selected={formData.childAgeRange === '0-6'}>
                          <UserCheck size={12} />
                        </Checkmark>
                        <ChildAgeRangeLabel
                          selected={formData.childAgeRange === '0-6'}
                          genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                        >
                          0-6
                        </ChildAgeRangeLabel>
                      </ChildAgeRangeBox>

                      <ChildAgeRangeBox
                        selected={formData.childAgeRange === '7-12'}
                        onClick={() => handleChildAgeRangeSelect('7-12')}
                        disabled={!formData.ageGroup || formData.ageGroup !== 'child'}
                        genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                      >
                        <Checkmark selected={formData.childAgeRange === '7-12'}>
                          <UserCheck size={12} />
                        </Checkmark>
                        <ChildAgeRangeLabel
                          selected={formData.childAgeRange === '7-12'}
                          genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                        >
                          7-12
                        </ChildAgeRangeLabel>
                      </ChildAgeRangeBox>

                      <ChildAgeRangeBox
                        selected={formData.childAgeRange === '13-17'}
                        onClick={() => handleChildAgeRangeSelect('13-17')}
                        disabled={!formData.ageGroup || formData.ageGroup !== 'child'}
                        genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                      >
                        <Checkmark selected={formData.childAgeRange === '13-17'}>
                          <UserCheck size={12} />
                        </Checkmark>
                        <ChildAgeRangeLabel
                          selected={formData.childAgeRange === '13-17'}
                          genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                        >
                          13-17
                        </ChildAgeRangeLabel>
                      </ChildAgeRangeBox>
                    </ChildAgeRangeContainer>
                    <input
                      type="hidden"
                      name="ageGroup"
                      value={formData.ageGroup}
                      required
                    />
                    <input
                      type="hidden"
                      name="childAgeRange"
                      value={formData.childAgeRange}
                    />
                  </AgeGroupSection>
                </GenderAgeGroupRow>
              </FormGroup>

              <FormGroup>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                  <CheckboxContainer style={{ flexShrink: 0 }}>
                    <Checkbox
                      type="checkbox"
                      id="hasCompanion"
                      name="hasCompanion"
                      checked={formData.hasCompanion}
                      onChange={handleChange}
                      disabled={!formData.ageGroup}
                    />
                    <CheckboxLabel htmlFor="hasCompanion" style={{ opacity: !formData.ageGroup ? 0.5 : 1 }}>
                      {t('visitor.withCompanion')}
                    </CheckboxLabel>
                  </CheckboxContainer>

                  <CompanionSection show={formData.hasCompanion}>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <div style={{ flex: '1', minWidth: '120px' }}>
                        <Select
                          id="companionRelationship"
                          name="companionRelationship"
                          value={formData.companionRelationship}
                          onChange={handleChange}
                          style={{ width: '100%' }}
                        >
                          <option value="">{t('visitor.relationship')}</option>
                          <option value="parent">{t('visitor.parent')}</option>
                          <option value="friend">{t('visitor.friend')}</option>
                        </Select>
                      </div>

                      {(formData.companionRelationship === 'parent' || formData.companionRelationship === 'friend') && (
                        <div style={{ flex: '0 0 100px' }}>
                          <Input
                            type="number"
                            id="companionCount"
                            name="companionCount"
                            value={formData.companionCount}
                            onChange={handleChange}
                            min="1"
                            placeholder={t('visitor.howMany')}
                            style={{ padding: '0.625rem 0.875rem', fontSize: '0.875rem', borderRadius: '10px', width: '100%' }}
                          />
                        </div>
                      )}

                      <div style={{ flex: '0 0 80px' }}>
                        <Input
                          type="number"
                          id="companionAge"
                          name="companionAge"
                          value={formData.companionAge}
                          onChange={handleChange}
                          min="1"
                          max="120"
                          placeholder={t('visitor.age')}
                          style={{ padding: '0.625rem 0.875rem', fontSize: '0.875rem', borderRadius: '10px', width: '100%' }}
                        />
                      </div>

                      <div style={{ flex: '1', minWidth: '200px' }}>
                        <CheckboxContainer style={{ margin: 0 }}>
                          <Checkbox
                            type="checkbox"
                            id="companionWearsGlasses"
                            name="companionWearsGlasses"
                            checked={formData.companionWearsGlasses}
                            onChange={handleChange}
                          />
                          <CheckboxLabel htmlFor="companionWearsGlasses">
                            {t('visitor.currentlyWearsGlasses')}
                          </CheckboxLabel>
                        </CheckboxContainer>
                      </div>

                      {/* When glasses are worn and there is more than one companion,
                          ask how many of them wear glasses. */}
                      {formData.companionWearsGlasses && Number(formData.companionCount) > 1 && (
                        <div style={{ flex: '0 0 170px' }}>
                          <Input
                            type="number"
                            id="companionWearingCount"
                            name="companionWearingCount"
                            value={formData.companionWearingCount}
                            onChange={handleChange}
                            min="1"
                            max={formData.companionCount || undefined}
                            placeholder={t('visitor.howManyWear', 'How many wear glasses?')}
                            style={{ padding: '0.625rem 0.875rem', fontSize: '0.875rem', borderRadius: '10px', width: '100%' }}
                          />
                        </div>
                      )}
                    </div>
                  </CompanionSection>
                </div>
              </FormGroup>

              <FormGroup>
                <MotiveContainer>
                  <MotiveBox
                    selected={formData.motive === 'buying_eyeglasses'}
                    onClick={() => handleMotiveSelect('buying_eyeglasses')}
                    disabled={!formData.ageGroup || (formData.ageGroup === 'child' && !formData.childAgeRange)}
                    genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                  >
                    <Checkmark selected={formData.motive === 'buying_eyeglasses'}>
                      <UserCheck size={14} />
                    </Checkmark>
                    <MotiveIcon
                      selected={formData.motive === 'buying_eyeglasses'}
                      genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                    >
                      <Glasses size={20} />
                    </MotiveIcon>
                    <MotiveLabel
                      selected={formData.motive === 'buying_eyeglasses'}
                      genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                    >
                      {t('visitor.buyingEyeglasses')}
                    </MotiveLabel>
                  </MotiveBox>

                  <MotiveBox
                    selected={formData.motive === 'solar'}
                    onClick={() => handleMotiveSelect('solar')}
                    disabled={!formData.ageGroup || (formData.ageGroup === 'child' && !formData.childAgeRange)}
                    genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                  >
                    <Checkmark selected={formData.motive === 'solar'}>
                      <UserCheck size={14} />
                    </Checkmark>
                    <MotiveIcon
                      selected={formData.motive === 'solar'}
                      genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                    >
                      <Sun size={20} />
                    </MotiveIcon>
                    <MotiveLabel
                      selected={formData.motive === 'solar'}
                      genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                    >
                      {t('visitor.frameAndSolar')}
                    </MotiveLabel>
                  </MotiveBox>

                  <MotiveBox
                    selected={formData.motive === 'other_otc_sales'}
                    onClick={() => handleMotiveSelect('other_otc_sales')}
                    disabled={!formData.ageGroup || (formData.ageGroup === 'child' && !formData.childAgeRange)}
                    genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                  >
                    <Checkmark selected={formData.motive === 'other_otc_sales'}>
                      <UserCheck size={14} />
                    </Checkmark>
                    <MotiveIcon
                      selected={formData.motive === 'other_otc_sales'}
                      genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                    >
                      <ShoppingBag size={20} />
                    </MotiveIcon>
                    <MotiveLabel
                      selected={formData.motive === 'other_otc_sales'}
                      genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                    >
                      {t('visitor.otcSales')}
                    </MotiveLabel>
                  </MotiveBox>

                  <MotiveBox
                    selected={formData.motive === 'inquiry'}
                    onClick={() => handleMotiveSelect('inquiry')}
                    disabled={!formData.ageGroup || (formData.ageGroup === 'child' && !formData.childAgeRange)}
                    genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                  >
                    <Checkmark selected={formData.motive === 'inquiry'}>
                      <UserCheck size={14} />
                    </Checkmark>
                    <MotiveIcon
                      selected={formData.motive === 'inquiry'}
                      genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                    >
                      <HelpCircle size={20} />
                    </MotiveIcon>
                    <MotiveLabel
                      selected={formData.motive === 'inquiry'}
                      genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                    >
                      {t('visitor.inquiry')}
                    </MotiveLabel>
                  </MotiveBox>
                </MotiveContainer>
                <input
                  type="hidden"
                  name="motive"
                  value={formData.motive}
                  required
                />
              </FormGroup>

              <EyeglassesTypeContainer show={formData.motive === 'buying_eyeglasses'}>
                <EyeglassesTypeBox
                  selected={formData.eyeglassesType === 'prescription'}
                  onClick={() => handleEyeglassesTypeSelect('prescription')}
                  disabled={!formData.motive || formData.motive !== 'buying_eyeglasses'}
                  genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                >
                  <Checkmark selected={formData.eyeglassesType === 'prescription'}>
                    <UserCheck size={12} />
                  </Checkmark>
                  <EyeglassesTypeLabel
                    selected={formData.eyeglassesType === 'prescription'}
                    genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                  >
                    {t('visitor.eyeglassesPrescription')}
                  </EyeglassesTypeLabel>
                </EyeglassesTypeBox>

                <EyeglassesTypeBox
                  selected={formData.eyeglassesType === 'no_prescription'}
                  onClick={() => handleEyeglassesTypeSelect('no_prescription')}
                  disabled={!formData.motive || formData.motive !== 'buying_eyeglasses'}
                  genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                >
                  <Checkmark selected={formData.eyeglassesType === 'no_prescription'}>
                    <UserCheck size={12} />
                  </Checkmark>
                  <EyeglassesTypeLabel
                    selected={formData.eyeglassesType === 'no_prescription'}
                    genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                  >
                    {t('visitor.eyeglassesNoPrescription')}
                  </EyeglassesTypeLabel>
                </EyeglassesTypeBox>
              </EyeglassesTypeContainer>

              {/* Prescription Status Section - Only show when prescription is selected */}
              {formData.motive === 'buying_eyeglasses' && formData.eyeglassesType === 'prescription' && (
                <FormGroup>
                  <Label>{t('visitor.qPrescriptionStatus')} *</Label>
                  <PrescriptionStatusContainer show={formData.motive === 'buying_eyeglasses' && formData.eyeglassesType === 'prescription'}>
                    <PrescriptionStatusBox
                      selected={formData.prescriptionStatus === 'new'}
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        prescriptionStatus: 'new',
                        visionType: '',
                        // Clear all vision type specific fields
                        simpleVisionCorrectionType: '',
                        simpleVisionFarCorrection: '',
                        simpleVisionCloseUpCorrection: '',
                        progressiveType: '',
                        progressiveOldCorrection: '',
                        progressiveImpression: '',
                        verifyOldGlasses: false,
                        otherContactLensType: ''
                      }))}
                      genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                    >
                      <Checkmark selected={formData.prescriptionStatus === 'new'}>
                        <UserCheck size={12} />
                      </Checkmark>
                      <PreviousEyeglassesLabel
                        selected={formData.prescriptionStatus === 'new'}
                        genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                      >
                        {t('visitor.prescNew')}
                      </PreviousEyeglassesLabel>
                    </PrescriptionStatusBox>

                    <PrescriptionStatusBox
                      selected={formData.prescriptionStatus === 'already_wear'}
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        prescriptionStatus: prev.prescriptionStatus === 'already_wear' ? null : 'already_wear'
                      }))}
                      genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                    >
                      <Checkmark selected={formData.prescriptionStatus === 'already_wear'}>
                        <UserCheck size={12} />
                      </Checkmark>
                      <PreviousEyeglassesLabel
                        selected={formData.prescriptionStatus === 'already_wear'}
                        genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                      >
                        {t('visitor.prescAlreadyWear')}
                      </PreviousEyeglassesLabel>
                    </PrescriptionStatusBox>
                  </PrescriptionStatusContainer>

                  {/* Vision Type Selection - Only show when "Already Wear" is selected */}
                  {formData.prescriptionStatus === 'already_wear' && (
                    <FormGroup style={{ marginTop: '1rem' }}>
                      <Label>{t('visitor.qVisionType')} *</Label>
                      <VisionTypeContainer show={formData.prescriptionStatus === 'already_wear'}>
                        <VisionTypeBox
                          selected={formData.visionType === 'simple_vision'}
                          onClick={() => setFormData(prev => ({
                            ...prev,
                            visionType: prev.visionType === 'simple_vision' ? '' : 'simple_vision',
                            // Clear other vision type fields
                            progressiveType: '',
                            progressiveOldCorrection: '',
                            progressiveImpression: '',
                            verifyOldGlasses: false,
                            otherContactLensType: '',
                            // Clear simple vision fields. Non-seniors only have "Far",
                            // so auto-select it (the type selector is hidden for them).
                            simpleVisionCorrectionType: (prev.visionType !== 'simple_vision' && prev.ageGroup !== 'senior') ? 'far' : '',
                            simpleVisionFarRightEye: { sphere: '', cylinder: '', axis: '', add: '' },
                            simpleVisionFarLeftEye: { sphere: '', cylinder: '', axis: '', add: '' },
                            simpleVisionCloseUpRightEye: { sphere: '', cylinder: '', axis: '', add: '' },
                            simpleVisionCloseUpLeftEye: { sphere: '', cylinder: '', axis: '', add: '' }
                          }))}
                          genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                        >
                          <Checkmark selected={formData.visionType === 'simple_vision'}>
                            <UserCheck size={12} />
                          </Checkmark>
                          <VisionTypeLabel
                            selected={formData.visionType === 'simple_vision'}
                            genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                          >
                            {t('visitor.simpleVision')}
                          </VisionTypeLabel>
                        </VisionTypeBox>

                        {/* Progressive - Only show for seniors */}
                        {formData.ageGroup === 'senior' && (
                          <VisionTypeBox
                            selected={formData.visionType === 'progressive'}
                            onClick={() => setFormData(prev => ({
                              ...prev,
                              visionType: prev.visionType === 'progressive' ? '' : 'progressive',
                              // Clear simple vision and other fields
                              simpleVisionCorrectionType: '',
                              simpleVisionFarCorrection: '',
                              simpleVisionCloseUpCorrection: '',
                              otherContactLensType: ''
                            }))}
                            genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                          >
                            <Checkmark selected={formData.visionType === 'progressive'}>
                              <UserCheck size={12} />
                            </Checkmark>
                            <VisionTypeLabel
                              selected={formData.visionType === 'progressive'}
                              genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                            >
                              {t('visitor.progressive')}
                            </VisionTypeLabel>
                          </VisionTypeBox>
                        )}

                        <VisionTypeBox
                          selected={formData.visionType === 'other'}
                          onClick={() => setFormData(prev => ({
                            ...prev,
                            visionType: prev.visionType === 'other' ? '' : 'other',
                            // Clear simple vision and progressive fields
                            simpleVisionCorrectionType: '',
                            simpleVisionFarCorrection: '',
                            simpleVisionCloseUpCorrection: '',
                            progressiveType: '',
                            progressiveOldCorrection: '',
                            progressiveImpression: '',
                            verifyOldGlasses: false
                          }))}
                          genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                        >
                          <Checkmark selected={formData.visionType === 'other'}>
                            <UserCheck size={12} />
                          </Checkmark>
                          <VisionTypeLabel
                            selected={formData.visionType === 'other'}
                            genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                          >
                            {t('visitor.contactLenses')}
                          </VisionTypeLabel>
                        </VisionTypeBox>
                      </VisionTypeContainer>
                    </FormGroup>
                  )}

                  {/* Simple Vision Form */}
                  {formData.prescriptionStatus === 'already_wear' && formData.visionType === 'simple_vision' && (
                    <FormGroup style={{ marginTop: '1rem', padding: '1rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                      <Label style={{ marginBottom: '0.5rem', fontWeight: '600' }}>{t('visitor.simpleVisionDetails')} *</Label>

                      {/* Correction-type selector only for seniors (43+); non-seniors are Far only */}
                      {formData.ageGroup === 'senior' && (
                      <FormGroup>
                        <Label>{t('visitor.qCorrectionType')} *</Label>
                        <VisionTypeContainer show={true} style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                          <VisionTypeBox
                            selected={formData.simpleVisionCorrectionType === 'far'}
                            onClick={() => setFormData(prev => ({
                              ...prev,
                              simpleVisionCorrectionType: 'far',
                              simpleVisionCloseUpRightEye: { sphere: '', cylinder: '', axis: '', add: '' },
                              simpleVisionCloseUpLeftEye: { sphere: '', cylinder: '', axis: '', add: '' } // Clear close up if only far is selected
                            }))}
                            genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                          >
                            <Checkmark selected={formData.simpleVisionCorrectionType === 'far'}>
                              <UserCheck size={12} />
                            </Checkmark>
                            <VisionTypeLabel
                              selected={formData.simpleVisionCorrectionType === 'far'}
                              genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                            >
                              {t('rx.far')}
                            </VisionTypeLabel>
                          </VisionTypeBox>

                          {/* Close Up and Both only apply to seniors (43+) */}
                          {formData.ageGroup === 'senior' && (
                            <>
                              <VisionTypeBox
                                selected={formData.simpleVisionCorrectionType === 'closeup'}
                                onClick={() => setFormData(prev => ({
                                  ...prev,
                                  simpleVisionCorrectionType: 'closeup',
                                  simpleVisionFarRightEye: { sphere: '', cylinder: '', axis: '', add: '' },
                                  simpleVisionFarLeftEye: { sphere: '', cylinder: '', axis: '', add: '' } // Clear far if only close up is selected
                                }))}
                                genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                              >
                                <Checkmark selected={formData.simpleVisionCorrectionType === 'closeup'}>
                                  <UserCheck size={12} />
                                </Checkmark>
                                <VisionTypeLabel
                                  selected={formData.simpleVisionCorrectionType === 'closeup'}
                                  genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                                >
                                  {t('rx.closeUp')}
                                </VisionTypeLabel>
                              </VisionTypeBox>

                              <VisionTypeBox
                                selected={formData.simpleVisionCorrectionType === 'both'}
                                onClick={() => setFormData(prev => ({
                                  ...prev,
                                  simpleVisionCorrectionType: 'both'
                                }))}
                                genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                              >
                                <Checkmark selected={formData.simpleVisionCorrectionType === 'both'}>
                                  <UserCheck size={12} />
                                </Checkmark>
                                <VisionTypeLabel
                                  selected={formData.simpleVisionCorrectionType === 'both'}
                                  genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                                >
                                  {t('rx.both')}
                                </VisionTypeLabel>
                              </VisionTypeBox>
                            </>
                          )}
                        </VisionTypeContainer>
                      </FormGroup>
                      )}

                      {/* Show Far correction fields if Far or Both is selected */}
                      {(formData.simpleVisionCorrectionType === 'far' || formData.simpleVisionCorrectionType === 'both') && (
                        <FormGroup style={{ marginTop: '0.75rem' }}>
                          <Label style={{ marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.875rem' }}>{t('visitor.prevCorrectionFar')}</Label>
                          {renderCorrectionTable('simpleVisionFarRightEye', 'simpleVisionFarLeftEye', false)}
                        </FormGroup>
                      )}

                      {/* Show Close Up correction fields if Close Up or Both is selected */}
                      {(formData.simpleVisionCorrectionType === 'closeup' || formData.simpleVisionCorrectionType === 'both') && (
                        <FormGroup style={{ marginTop: '0.75rem' }}>
                          <Label style={{ marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.875rem' }}>{t('visitor.prevCorrectionCloseUp')}</Label>
                          {renderCorrectionTable('simpleVisionCloseUpRightEye', 'simpleVisionCloseUpLeftEye', false)}
                        </FormGroup>
                      )}
                    </FormGroup>
                  )}

                  {/* Progressive Form - Only show for seniors */}
                  {formData.prescriptionStatus === 'already_wear' && formData.visionType === 'progressive' && formData.ageGroup === 'senior' && (
                    <FormGroup style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                      <Label style={{ marginBottom: '0.375rem', fontWeight: '600', fontSize: '0.875rem' }}>{t('visitor.progressiveDetails')} *</Label>

                      <FormGroup>
                        <Label htmlFor="progressiveType">{t('visitor.type')} *</Label>
                        <Input
                          type="text"
                          id="progressiveType"
                          name="progressiveType"
                          value={formData.progressiveType}
                          onChange={handleChange}
                          placeholder={t('visitor.enterProgressiveType')}
                          required
                        />
                      </FormGroup>

                      <FormGroup>
                        <Label>{t('visitor.oldCorrectionProgressive')} *</Label>
                        {renderCorrectionTable('progressiveOldRightEye', 'progressiveOldLeftEye', true)}
                      </FormGroup>

                      <FormGroup>
                        <Label htmlFor="progressiveImpression">{t('visitor.impression')} *</Label>
                        <TextArea
                          id="progressiveImpression"
                          name="progressiveImpression"
                          value={formData.progressiveImpression}
                          onChange={handleChange}
                          placeholder={t('visitor.enterImpressionDetails')}
                          required
                        />
                      </FormGroup>

                      <FormGroup>
                        <CheckboxContainer>
                          <Checkbox
                            type="checkbox"
                            id="verifyOldGlasses"
                            name="verifyOldGlasses"
                            checked={formData.verifyOldGlasses}
                            onChange={handleChange}
                          />
                          <CheckboxLabel htmlFor="verifyOldGlasses">
                            {t('visitor.verifyOldGlassesCenterAnd')}
                          </CheckboxLabel>
                        </CheckboxContainer>
                      </FormGroup>
                    </FormGroup>
                  )}

                  {/* Contact Lenses Form — shown when the user picks
                      "Contact Lenses" as their current correction type. */}
                  {formData.prescriptionStatus === 'already_wear' && formData.visionType === 'other' && (
                    <FormGroup style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                      <Label style={{ marginBottom: '0.375rem', fontWeight: '600', fontSize: '0.875rem' }}>{t('visitor.contactLensType')}</Label>

                      <VisionTypeContainer show={true} style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                        <VisionTypeBox
                          selected={formData.otherContactLensType === 'soft'}
                          onClick={() => setFormData(prev => ({
                            ...prev,
                            otherContactLensType: 'soft'
                          }))}
                          genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                        >
                          <Checkmark selected={formData.otherContactLensType === 'soft'}>
                            <UserCheck size={12} />
                          </Checkmark>
                          <VisionTypeLabel
                            selected={formData.otherContactLensType === 'soft'}
                            genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                          >
                            {t('visitor.contactSoft')}
                          </VisionTypeLabel>
                        </VisionTypeBox>

                        <VisionTypeBox
                          selected={formData.otherContactLensType === 'hard'}
                          onClick={() => setFormData(prev => ({
                            ...prev,
                            otherContactLensType: 'hard'
                          }))}
                          genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                        >
                          <Checkmark selected={formData.otherContactLensType === 'hard'}>
                            <UserCheck size={12} />
                          </Checkmark>
                          <VisionTypeLabel
                            selected={formData.otherContactLensType === 'hard'}
                            genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                          >
                            {t('visitor.contactHard')}
                          </VisionTypeLabel>
                        </VisionTypeBox>

                        <VisionTypeBox
                          selected={formData.otherContactLensType === 'therapeutic'}
                          onClick={() => setFormData(prev => ({
                            ...prev,
                            otherContactLensType: 'therapeutic'
                          }))}
                          genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                        >
                          <Checkmark selected={formData.otherContactLensType === 'therapeutic'}>
                            <UserCheck size={12} />
                          </Checkmark>
                          <VisionTypeLabel
                            selected={formData.otherContactLensType === 'therapeutic'}
                            genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                          >
                            {t('visitor.contactTherapeutic')}
                          </VisionTypeLabel>
                        </VisionTypeBox>
                      </VisionTypeContainer>
                    </FormGroup>
                  )}
                </FormGroup>
              )}

              <input
                type="hidden"
                name="eyeglassesType"
                value={formData.eyeglassesType}
              />
              <input
                type="hidden"
                name="hadPreviousEyeglasses"
                value={formData.hadPreviousEyeglasses}
              />
              <input
                type="hidden"
                name="previousPrescription"
                value={formData.previousPrescription}
              />
              <input
                type="hidden"
                name="prescriptionStatus"
                value={formData.prescriptionStatus || ''}
              />
              <input
                type="hidden"
                name="visionType"
                value={formData.visionType}
              />
              <input
                type="hidden"
                name="simpleVisionCorrectionType"
                value={formData.simpleVisionCorrectionType}
              />
              <input
                type="hidden"
                name="progressiveType"
                value={formData.progressiveType}
              />
              <input
                type="hidden"
                name="progressiveOldCorrection"
                value={formData.progressiveOldCorrection}
              />
              <input
                type="hidden"
                name="progressiveImpression"
                value={formData.progressiveImpression}
              />
              <input
                type="hidden"
                name="verifyOldGlasses"
                value={formData.verifyOldGlasses}
              />
              <input
                type="hidden"
                name="otherContactLensType"
                value={formData.otherContactLensType}
              />

              <InquiryTypeContainer show={formData.motive === 'inquiry'}>
                <InquiryTypeBox
                  selected={formData.inquiryType === 'curious'}
                  onClick={() => handleInquiryTypeSelect('curious')}
                  disabled={!formData.motive || formData.motive !== 'inquiry'}
                  genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                >
                  <Checkmark selected={formData.inquiryType === 'curious'}>
                    <UserCheck size={12} />
                  </Checkmark>
                  <InquiryTypeLabel
                    selected={formData.inquiryType === 'curious'}
                    genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                  >
                    {t('visitor.inquiryCurious')}
                  </InquiryTypeLabel>
                </InquiryTypeBox>

                <InquiryTypeBox
                  selected={formData.inquiryType === 'search_for_info'}
                  onClick={() => handleInquiryTypeSelect('search_for_info')}
                  disabled={!formData.motive || formData.motive !== 'inquiry'}
                  genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                >
                  <Checkmark selected={formData.inquiryType === 'search_for_info'}>
                    <UserCheck size={12} />
                  </Checkmark>
                  <InquiryTypeLabel
                    selected={formData.inquiryType === 'search_for_info'}
                    genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                  >
                    {t('visitor.inquirySearch')}
                  </InquiryTypeLabel>
                </InquiryTypeBox>
              </InquiryTypeContainer>
              <input
                type="hidden"
                name="inquiryType"
                value={formData.inquiryType}
              />

              {(formData.motive === 'buying_eyeglasses' || formData.motive === 'solar' || formData.motive === 'inquiry' || formData.motive === 'other_otc_sales') && (
                <FormGroup>
                  <Button
                    type="button"
                    onClick={handleValidateAndNavigate}
                    disabled={loading || (formData.motive === 'buying_eyeglasses' && (!formData.eyeglassesType || (formData.eyeglassesType === 'prescription' && formData.prescriptionStatus === null)))}
                    genderColor={formData.gender === 'female' ? '#ec4899' : formData.gender === 'male' ? '#3b82f6' : '#667eea'}
                  >
                    {loading ? (
                      <>
                        <LoadingSpinner />
                        {t('visitor.processing')}
                      </>
                    ) : (
                      <>
                        {t('visitor.validateContinue')}
                        <ArrowRight size={20} />
                      </>
                    )}
                  </Button>
                </FormGroup>
              )}
            </Form>
          </Card>
        </ContentWrapper>
      </Container>
    </SalesLayout>
  );
};

export default VisitorEntry;
